import { Phone, Mail } from "lucide-react";
import type { CoordonneesComponent } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, resolveImageValue } from "../../utils/imageOptions";

const TITLE_COLOR = "#00B862";
const PHONE_BG = "#FFC105";
const EMAIL_BG = "#00B862";

/** Contact details block renderer with image, phone, email and address. */
export function CoordonneesRenderer({ component }: { component: CoordonneesComponent }) {
  const {
    imageSrc,
    imageAlt,
    topTitle,
    introParagraph,
    phoneHref,
    emailHref,
    secondTitle,
    addressLinkText,
    addressLinkHref,
    addressLines = [],
  } = component.content;

  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const resolvedPhone = resolveLinkHref(phoneHref);
  const resolvedEmail = resolveLinkHref(emailHref);
  const resolvedAddressLink = resolveLinkHref(addressLinkHref);

  return (
    <section>
      <div className="grid min-w-0 grid-cols-1 gap-0 md:grid-cols-2 md:items-stretch">
        <div
          className="relative min-h-0 overflow-hidden md:min-w-0"
          style={{ minHeight: "min(50vmin, 280px)", minWidth: "min(280px, 100%)" }}
        >
          <RuntimeImage
            src={src}
            alt={imageAlt}
            className="absolute inset-0 h-full w-full object-cover"
            style={objectPositionStyle}
            priority
          />
        </div>
        <div className="flex flex-col justify-center p-6 text-left md:p-8" style={{ backgroundColor: "#E6F4EA" }}>
          <h3 className="text-[17px] xl:text-[19px] font-semibold md:text-lg" style={{ color: TITLE_COLOR }}>
            {topTitle}
          </h3>
          {introParagraph ? (
            <p className="mt-2 text-sm xl:text-base leading-6 text-[#121212] md:text-base">{introParagraph}</p>
          ) : null}
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            {resolvedPhone ? (
              <RuntimeLink
                href={resolvedPhone}
                className="coordonnees-icon-button shrink-0 rounded-full text-white transition hover:opacity-90"
                style={{
                  backgroundColor: PHONE_BG,
                  width: "2.75rem",
                  height: "2.75rem",
                  display: "grid",
                  placeItems: "center",
                }}
                aria-label="Téléphone"
              >
                <Phone className="h-5 w-5 block" aria-hidden style={{ margin: 0 }} />
              </RuntimeLink>
            ) : null}
            {resolvedEmail ? (
              <RuntimeLink
                href={resolvedEmail}
                className="coordonnees-icon-button shrink-0 rounded-full text-white transition hover:opacity-90"
                style={{
                  backgroundColor: EMAIL_BG,
                  width: "2.75rem",
                  height: "2.75rem",
                  display: "grid",
                  placeItems: "center",
                }}
                aria-label="E-mail"
              >
                <Mail className="h-5 w-5 block" aria-hidden style={{ margin: 0 }} />
              </RuntimeLink>
            ) : null}
          </div>
          {secondTitle ? (
            <h4 className="mt-6 text-[17px] xl:text-[19px] font-semibold md:text-lg" style={{ color: TITLE_COLOR }}>
              {secondTitle}
            </h4>
          ) : null}
          <div className="mt-2 space-y-1 text-sm xl:text-base text-[#121212] md:text-base">
            {addressLinkText && resolvedAddressLink ? (
              <RuntimeLink
                href={resolvedAddressLink}
                className="underline"
                style={{ color: TITLE_COLOR }}
              >
                {addressLinkText}
              </RuntimeLink>
            ) : addressLinkText ? (
              <span>{addressLinkText}</span>
            ) : null}
            {addressLines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
